import { MODAL_TYPE } from 'config/Constants';
import { useStore } from 'hooks/useStore';
import { useYJSCollaboration } from 'hooks/useYJSCollaboration';
import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import RoomAccordion from './index';

import { toast } from 'react-toastify';
interface RoomAccordionWithWebSocketProps {
  sendEditingStatus: (jobId: string, roomId: string, isEditing: boolean) => void;
  sendActiveUserUpdate: (roomId: string, action: 'add' | 'remove') => void;
  isConnected: boolean;
}

const RoomAccordionWithWebSocket: React.FC<RoomAccordionWithWebSocketProps> = ({
  sendEditingStatus,
  sendActiveUserUpdate,
  isConnected
}) => {
  const { JobPasteStore, GlobalModalStore } = useStore();
  const { startEditingRoom, stopEditingRoom } = useYJSCollaboration();
  const environment = import.meta.env.VITE_ENVIRONMENT;

  // Enhanced edit click handler that notifies WebSocket and YJS
  const handleEditClick = useCallback(
    async (roomId: string) => {
      try {
        // Notify WebSocket that user is starting to edit this room
        if (isConnected) {
          // Send active user update
          sendActiveUserUpdate(roomId, 'add');

          // Send editing status for all jobs in the room
          const room = JobPasteStore.jobs?.find(r => r.id === roomId);
          if (room) {
            room.jobs.forEach(job => {
              sendEditingStatus(job.jobId, roomId, true);
            });
          }
        }

        // Notify YJS collaboration service
        const room = JobPasteStore.jobs?.find(r => r.id === roomId);
        if (room) {
          startEditingRoom(room.name, room.jobs[0]?.id); // Start with first job ID if available
        }

        // Call the original edit function (now async)
        await JobPasteStore.enableEditMode(roomId);
      } catch (error) {
        if (environment === 'development') {
          console.error('Error in handleEditClick:', error);
        }
        toast.error('Something went wrong while going to edit mode!');
      }
    },
    [sendEditingStatus, sendActiveUserUpdate, isConnected, JobPasteStore, startEditingRoom]
  );

  // Enhanced cancel click handler that notifies WebSocket and YJS
  const handleCancelClick = useCallback(async () => {
    // Store the current room info before disabling edit mode
    const currentRoomId = JobPasteStore.currentRoomInEditMode;
    const currentRoom = currentRoomId
      ? JobPasteStore.jobs?.find(r => r.id === currentRoomId)
      : null;

    // Generate revert payload BEFORE any cleanup operations
    JobPasteStore.disableEditMode();

    // Notify WebSocket that user is stopping editing
    if (isConnected && currentRoomId) {
      // Send active user update
      sendActiveUserUpdate(currentRoomId, 'remove');
      // await JobPasteStore.fetchAllRoomsFromRedis();

      const room = JobPasteStore.jobs?.find(r => r.id === currentRoomId);
      if (room) {
        room.jobs.forEach(job => {
          sendEditingStatus(job.jobId, currentRoomId, false);
        });
      }
    }

    // Notify YJS collaboration service
    if (currentRoom) {
      stopEditingRoom(currentRoom.name);
    }
  }, [sendEditingStatus, sendActiveUserUpdate, isConnected, JobPasteStore, stopEditingRoom]);

  // Enhanced save click handler that notifies WebSocket and YJS
  const handleSaveClick = useCallback(
    (roomId: string) => {
      // Check for overrides FIRST before removing user from active users
      const didOverrideHappen = JobPasteStore.onSaveOfEditedJobsInARoom(roomId);

      if (didOverrideHappen !== undefined) {
        // Overrides detected - DON'T remove user yet, modal will handle it
        // User stays in active users list until they complete the reason capture
        GlobalModalStore.toggleModal(MODAL_TYPE.PPS_JOB_PASTE_OVERRIDE_CAPTURE_MODAL);
      } else {
        // No overrides detected - remove user immediately and proceed with save
        if (isConnected) {
          // Send active user update to remove user from active users list
          sendActiveUserUpdate(roomId, 'remove');

          const room = JobPasteStore.jobs?.find(r => r.id === roomId);
          if (room) {
            room.jobs.forEach(job => {
              sendEditingStatus(job.jobId, roomId, false);
            });
          }
        }

        // Notify YJS collaboration service
        const room = JobPasteStore.jobs?.find(r => r.id === roomId);
        if (room) {
          stopEditingRoom(room.name);
        }

        // Proceed with direct save since no overrides are required
        JobPasteStore.saveEditedRoomJobs(roomId);
      }
    },
    [
      sendEditingStatus,
      sendActiveUserUpdate,
      isConnected,
      JobPasteStore,
      GlobalModalStore,
      stopEditingRoom
    ]
  );

  return (
    <RoomAccordion
      onEditClick={handleEditClick}
      onCancelClick={handleCancelClick}
      onSaveClick={handleSaveClick}
    />
  );
};

export default observer(RoomAccordionWithWebSocket);
